import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';
import { useUpdateUser } from '@services/useUpdateUser';
import { useGetUser } from '@services/useGetUser';

interface ProfileFormErrors {
    general: string;
    name: string[];
    email: string[];
    phone: string[];
    password: string[];
    password_confirmation: string[];
}

export const MyProfileForm: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { t } = useTranslation();
    const { user } = useData();

    const {
        request: requestUpdateUser,
        errorMessage,
        validationErrors,
        success,
        successMessage,
        lastResponseTime,
        loading,
    } = useUpdateUser();

    const { request: requestGetUser } = useGetUser();

    const translations = {
        nameLabel: t('my-profile.form.name.label'),
        emailLabel: t('my-profile.form.email.label'),
        phoneLabel: t('my-profile.form.phone.label'),
        passwordLabel: t('my-profile.form.password.label'),
        passwordConfirmationLabel: t('my-profile.form.password-confirmation.label'),
        passwordHint: t('my-profile.form.password.hint'),
        nameRequiredError: t('my-profile.form.name.required-error'),
        emailRequiredError: t('my-profile.form.email.required-error'),
        submitButton: t('my-profile.form.submit.button'),
        successMessage: t('my-profile.form.success'),
    };

    const formRef = useRef<HTMLFormElement>(null);

    // Form state
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');

    // Populate form fields from user data
    useEffect(() => {
        if (user?.data) {
            setName(user.data.name || '');
            setEmail(user.data.email || '');
            setPhone(user.data.phone || '');
        }
    }, [user]);

    // Errors state
    const [errors, setErrors] = useState<ProfileFormErrors>({
        general: '',
        name: [],
        email: [],
        phone: [],
        password: [],
        password_confirmation: [],
    });

    const validateForm = (): ProfileFormErrors => {
        const newErrors: ProfileFormErrors = {
            general: '',
            name: [],
            email: [],
            phone: [],
            password: [],
            password_confirmation: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !email.trim() && newErrors.email.push(translations.emailRequiredError);

        setErrors(newErrors);
        return newErrors;
    };

    const hasNoErrors = (errors: ProfileFormErrors): boolean => {
        return (
            errors.name.length === 0 &&
            errors.email.length === 0 &&
            errors.phone.length === 0 &&
            errors.password.length === 0 &&
            errors.password_confirmation.length === 0
        );
    };

    const handleOnClickSave = () => {
        const validationErrors = validateForm();

        if (hasNoErrors(validationErrors)) {
            formRef?.current?.requestSubmit();
        }
    };

    const submitForm = async () => {
        const input: Record<string, string> = {
            name,
            email,
            phone,
        };

        // Only include password if user wants to change it
        if (password) {
            input.password = password;
            input.password_confirmation = passwordConfirmation;
        }

        await requestUpdateUser(input);
    };

    // Handle response
    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage || validationErrors) {
                setErrors({
                    general: errorMessage || '',
                    name: validationErrors?.name || [],
                    email: validationErrors?.email || [],
                    phone: validationErrors?.phone || [],
                    password: validationErrors?.password || [],
                    password_confirmation: validationErrors?.password_confirmation || [],
                });
            } else if (success) {
                showDefaultNotification({
                    type: 'success',
                    message: successMessage || translations.successMessage,
                });
                // Clear password fields after successful update
                setPassword('');
                setPasswordConfirmation('');
                // Refresh user data
                requestGetUser();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* Nome */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.nameLabel}*`}
                        value={name}
                        onChange={(value) => setName(value)}
                        disabled={loading}
                        state={errors.name.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.name}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Email */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.email}
                        variant={InputVariants.labeledInput}
                        label={`${translations.emailLabel}*`}
                        value={email}
                        onChange={(value) => setEmail(value)}
                        disabled={loading}
                        state={errors.email.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.email}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Telefone */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.tel}
                        variant={InputVariants.labeledInput}
                        label={translations.phoneLabel}
                        value={phone}
                        onChange={(value) => setPhone(value)}
                        disabled={loading}
                        state={errors.phone.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.phone}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Password */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.password}
                        variant={InputVariants.labeledInput}
                        label={translations.passwordLabel}
                        value={password}
                        onChange={(value) => setPassword(value)}
                        disabled={loading}
                        state={errors.password.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.password}
                        placeholder={translations.passwordHint}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Password Confirmation */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.password}
                        variant={InputVariants.labeledInput}
                        label={translations.passwordConfirmationLabel}
                        value={passwordConfirmation}
                        onChange={(value) => setPasswordConfirmation(value)}
                        disabled={loading}
                        state={errors.password_confirmation.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.password_confirmation}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
            </div>

            {/* Submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={loading}
                    onClick={() => handleOnClickSave()}
                />
            </div>
        </Form>
    );
};
